import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowRight, CheckCircle, XCircle, Eye, EyeOff,
  ChevronLeft, ChevronRight, Clock, Target, Brain,
  Calculator, BookOpen, BarChart3, Filter, Zap, ArrowLeft
} from "lucide-react";

const BankingUniversalPracticePage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { toast } = useToast();
  const isPro = user?.role === "PRO" || user?.banking_role === "PRO";

  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [difficulty, setDifficulty] = useState("all");
  const [numQuestions, setNumQuestions] = useState(20);

  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showSolution, setShowSolution] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [questionTimes, setQuestionTimes] = useState({});

  const sectionIcons = {
    quant: Calculator,
    reasoning: Brain,
    english: BookOpen,
    di: BarChart3,
    banking_awareness: Target,
    computer: Zap
  };

  const sectionLabels = {
    quant: "Quantitative Aptitude",
    reasoning: "Logical Reasoning",
    english: "English Language",
    di: "Data Interpretation",
    banking_awareness: "Banking & Financial Awareness",
    computer: "Computer Knowledge"
  };

  useEffect(() => { loadSections(); }, []);

  const loadSections = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/banking/questions/banking-sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSections(data.sections || []);
    } catch {
      toast({ variant: "destructive", description: "Could not load sections" });
    } finally {
      setLoading(false);
    }
  };

  const startPractice = async () => {
    if (!selectedSection) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        section: selectedSection.id,
        difficulty,
        num_questions: numQuestions.toString()
      });
      const res = await fetch(`${API_BASE}/banking/questions/banking-practice/start?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setQuestions(data.questions || []);
      setCurrentQ(0);
      setAnswers({});
      setShowSolution(false);
      setResults(null);
      setStartTime(Date.now());
      setQuestionStartTime(Date.now());
      setQuestionTimes({});
      toast({ description: `${data.num_questions} questions loaded!` });
    } catch {
      toast({ variant: "destructive", description: "Could not load questions" });
    } finally {
      setLoading(false);
    }
  };

  const submitPractice = async () => {
    setLoading(true);
    const finalQT = { ...questionTimes };
    if (questions[currentQ]?.id && questionStartTime) {
      finalQT[questions[currentQ].id] = (finalQT[questions[currentQ].id] || 0) + Math.round((Date.now() - questionStartTime) / 1000);
    }
    try {
      const timeTaken = Math.round((Date.now() - startTime) / 1000);
      const res = await fetch(`${API_BASE}/banking/questions/banking-practice/submit/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers, time_taken: timeTaken })
      });
      const data = await res.json();

      // Calculate results client-side since banking endpoint doesn't score
      let score = 0;
      const resultDetails = questions.map(q => {
        const userAnswer = answers[q.id];
        const isCorrect = userAnswer === q.correct_answer;
        if (isCorrect) score++;
        return {
          question_id: q.id,
          is_correct: isCorrect,
          correct_answer: q.correct_answer,
          explanation: q.explanation || ""
        };
      });
      const percentage = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;

      // Analytics (non-blocking)
      fetch(`${API_BASE}/universal-analytics/submit-detailed`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionId,
          exam_type: "banking",
          section: selectedSection?.id || "quantitative_aptitude",
          attempts: questions.map(q => ({
            question_id: q.id, selected_answer: answers[q.id] || null,
            time_spent: finalQT[q.id] || 60, topic: q.topic,
            sub_topic: q.sub_topic, difficulty: q.difficulty
          })),
          total_time: timeTaken
        })
      }).catch(() => {});

      setResults({ score, total: questions.length, percentage, time_taken: timeTaken, results: resultDetails });
      toast({ description: `Score: ${score}/${questions.length} (${percentage}%)` });
    } catch {
      toast({ variant: "destructive", description: "Could not submit answers" });
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = (newIdx) => {
    if (questions[currentQ]?.id && questionStartTime) {
      const t = Math.round((Date.now() - questionStartTime) / 1000);
      setQuestionTimes(prev => ({ ...prev, [questions[currentQ].id]: (prev[questions[currentQ].id] || 0) + t }));
    }
    setCurrentQ(newIdx);
    setShowSolution(false);
    setQuestionStartTime(Date.now());
  };

  const handleBack = () => {
    if (questions.length > 0 && !results) {
      if (window.confirm("Exit practice? Your progress will be lost.")) {
        setQuestions([]); setSelectedSection(null);
      }
    } else if (selectedSection && !questions.length) {
      setSelectedSection(null);
    } else {
      navigate("/banking");
    }
  };

  const currentQuestion = questions[currentQ];
  const difficultyColors = {
    easy: "bg-green-100 text-green-800 border-green-200",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
    hard: "bg-orange-100 text-orange-800 border-orange-200",
    advanced: "bg-red-100 text-red-800 border-red-200"
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">

        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={handleBack} data-testid="practice-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-4 py-2 text-sm border-emerald-500 text-emerald-600">
              <Zap className="w-3 h-3 mr-1" /> Universal Bank
            </Badge>
            <Badge variant="outline" className={`px-4 py-2 text-sm ${isPro ? 'border-slate-900 text-slate-900' : 'border-slate-300 text-slate-500'}`}>
              {isPro ? "PRO" : "FREE"}
            </Badge>
          </div>
        </div>

        {/* Title */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Target className="w-6 h-6 text-emerald-600" />
            Banking MCQ Practice
          </h1>
          <p className="text-slate-500 mt-1">Bank exam-standard MCQs from Universal Bank - Easy to Advanced</p>
        </div>

        {/* Section Selection */}
        {!selectedSection && (
          <div className="space-y-6">
            <h2 className="text-base font-medium text-slate-700">Select a Section</h2>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mb-3" />
                <p className="text-slate-600">Loading sections...</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {sections.map((section) => {
                  const Icon = sectionIcons[section.id] || Target;
                  return (
                    <Card
                      key={section.id}
                      className="group cursor-pointer border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all"
                      onClick={() => setSelectedSection(section)}
                      data-testid={`section-${section.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
                            <Icon className="w-6 h-6" />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {section.total_questions} Qs
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-slate-900 text-lg mb-1">
                          {sectionLabels[section.id] || section.name}
                        </h3>
                        <div className="flex items-center text-sm text-slate-500 group-hover:text-emerald-600 transition-colors">
                          Start Practice <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Config */}
        {selectedSection && questions.length === 0 && !loading && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setSelectedSection(null)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Change Section
              </Button>
              <h2 className="text-xl font-semibold text-slate-900">
                {sectionLabels[selectedSection.id] || selectedSection.name}
              </h2>
            </div>
            <Card className="border-slate-200">
              <CardContent className="p-6 space-y-6">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Filter className="w-4 h-4" /> Difficulty Level
                  </label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[{ value: "all", label: "All Levels" }, { value: "easy", label: "Easy" }, { value: "medium", label: "Medium" }, { value: "hard", label: "Hard" }, { value: "advanced", label: "Advanced" }].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setDifficulty(opt.value)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${difficulty === opt.value ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                        data-testid={`difficulty-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" /> Number of Questions
                  </label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[10, 20, 30, 50].map((num) => (
                      <button
                        key={num}
                        onClick={() => setNumQuestions(num)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${numQuestions === num ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                        data-testid={`num-questions-${num}`}
                      >
                        {num} Questions
                      </button>
                    ))}
                  </div>
                  {!isPro && numQuestions > 10 && (
                    <p className="text-xs text-amber-600 mt-2">FREE users are limited to 10 questions. Upgrade to PRO for more.</p>
                  )}
                </div>
                <Button onClick={startPractice} className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base" data-testid="start-practice-btn">
                  <Zap className="w-5 h-5 mr-2" /> Start Practice
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loading */}
        {loading && selectedSection && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mb-4" />
            <p className="text-lg font-semibold text-slate-900">Loading Questions</p>
            <p className="text-sm text-slate-500">Preparing bank exam-standard MCQs...</p>
          </div>
        )}

        {/* Practice Questions */}
        {questions.length > 0 && currentQuestion && (
          <div className="space-y-6">
            {/* Question Navigator */}
            <div className="flex flex-wrap gap-2 justify-center p-4 bg-slate-50 rounded-xl">
              {questions.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuestionChange(idx)}
                  className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                    currentQ === idx
                      ? "bg-emerald-600 text-white ring-2 ring-emerald-300"
                      : answers[questions[idx]?.id]
                        ? results
                          ? results.results?.find(r => r.question_id === questions[idx]?.id)?.is_correct
                            ? "bg-green-200 text-green-800"
                            : "bg-red-200 text-red-800"
                          : "bg-emerald-100 text-emerald-800"
                        : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                  data-testid={`question-nav-${idx}`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>

            {/* Question Card */}
            <Card className="border-emerald-100 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-emerald-100 text-emerald-800">Q {currentQ + 1} / {questions.length}</Badge>
                    <Badge variant="outline" className={difficultyColors[currentQuestion.difficulty] || "border-slate-200"}>
                      {currentQuestion.difficulty?.toUpperCase()}
                    </Badge>
                  </div>
                  {startTime && !results && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Clock className="w-4 h-4" />
                      {Math.round((Date.now() - startTime) / 60000)} min
                    </div>
                  )}
                </div>

                <div className="text-xs text-slate-500 mb-3">
                  Topic: {currentQuestion.sub_topic?.replace(/_/g, ' ').toUpperCase()}
                </div>

                <p className="text-lg text-slate-900 mb-6 leading-relaxed whitespace-pre-wrap">{currentQuestion.question}</p>

                <div className="space-y-3">
                  {currentQuestion.options?.map((option) => {
                    const optKey = typeof option === 'object' ? option.key : String.fromCharCode(97 + currentQuestion.options.indexOf(option));
                    const optText = typeof option === 'object' ? option.text : option;
                    const isSelected = answers[currentQuestion.id] === optKey;
                    const resultItem = results?.results?.find(r => r.question_id === currentQuestion.id);
                    const isCorrect = resultItem?.correct_answer === optKey;
                    const showRes = results || showSolution;

                    return (
                      <button
                        key={optKey}
                        onClick={() => { if (!results) setAnswers({ ...answers, [currentQuestion.id]: optKey }); }}
                        disabled={!!results}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          showRes && isCorrect ? "border-green-500 bg-green-50"
                            : showRes && isSelected && !isCorrect ? "border-red-500 bg-red-50"
                            : isSelected ? "border-emerald-500 bg-emerald-50"
                            : "border-slate-200 hover:border-emerald-300 hover:bg-slate-50"
                        }`}
                        data-testid={`option-${optKey}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              showRes && isCorrect ? "bg-green-500 text-white"
                                : showRes && isSelected && !isCorrect ? "bg-red-500 text-white"
                                : isSelected ? "bg-emerald-500 text-white"
                                : "bg-slate-100 text-slate-600"
                            }`}>
                              {optKey.toUpperCase()}
                            </span>
                            <span className="text-slate-700">{optText}</span>
                          </span>
                          {showRes && isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
                          {showRes && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {answers[currentQuestion.id] && !results && (
                  <Button variant="outline" className="mt-4" onClick={() => setShowSolution(!showSolution)}>
                    {showSolution ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    {showSolution ? "Hide" : "Show"} Solution
                  </Button>
                )}

                {(showSolution || results) && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <p className="text-xs font-semibold text-blue-700 mb-2">EXPLANATION</p>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">
                      {results?.results?.find(r => r.question_id === currentQuestion.id)?.explanation || "Solution will be shown after submission."}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Navigation & Submit */}
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => handleQuestionChange(Math.max(0, currentQ - 1))} disabled={currentQ === 0}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              {!results && (
                <Button onClick={submitPractice} className="bg-emerald-600 hover:bg-emerald-700" disabled={Object.keys(answers).length < questions.length} data-testid="submit-practice-btn">
                  Submit ({Object.keys(answers).length}/{questions.length})
                </Button>
              )}
              {results && (
                <div className="text-center px-6 py-3 rounded-xl bg-emerald-50">
                  <p className="text-2xl font-bold text-emerald-900">{results.score}/{results.total}</p>
                  <p className="text-sm text-emerald-600">{results.percentage}% Accuracy</p>
                </div>
              )}
              <Button variant="outline" onClick={() => handleQuestionChange(Math.min(questions.length - 1, currentQ + 1))} disabled={currentQ === questions.length - 1}>
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Results Summary */}
            {results && (
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Practice Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-white rounded-xl">
                      <p className="text-2xl font-bold text-emerald-600">{results.score}</p>
                      <p className="text-xs text-slate-500">Correct</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl">
                      <p className="text-2xl font-bold text-red-600">{results.total - results.score}</p>
                      <p className="text-xs text-slate-500">Incorrect</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl">
                      <p className="text-2xl font-bold text-blue-600">{results.percentage}%</p>
                      <p className="text-xs text-slate-500">Accuracy</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl">
                      <p className="text-2xl font-bold text-slate-600">{Math.round(results.time_taken / 60)} min</p>
                      <p className="text-xs text-slate-500">Time Taken</p>
                    </div>
                  </div>
                  {results.weak_topics?.length > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-sm font-medium text-amber-800 mb-2">Topics to Improve:</p>
                      <div className="flex flex-wrap gap-2">
                        {results.weak_topics.map((topic, idx) => (
                          <Badge key={idx} variant="outline" className="border-amber-300 text-amber-700">{topic.replace(/_/g, ' ')}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={() => { setQuestions([]); setSelectedSection(null); setResults(null); }}>
                    Practice Again
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BankingUniversalPracticePage;
